import { useLayoutEffect } from 'react';
import { setUiStrings } from '@parsina/framework';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

/**
 * Hands the design kit this panel's catalogue.
 *
 * WHY THIS EXISTS
 * A few kit components own text that never passes through a prop — the calendar
 * chrome inside `JalaliDatePicker`, the labels `DateRangeFilter` draws around
 * it, the message a mistyped date produces. They used to be Persian literals,
 * which is why an English panel showed «پاک کردن تاریخ» on a button next to
 * "Clear" (docs/I18N.md § "Known gap: the shared design system").
 *
 * The kit now asks a registry for those strings and falls back to the same
 * Persian it always rendered. This component is the answer: it points the
 * registry at `t` / `tList` / `formatNumber`, so the kit speaks whatever the
 * panel speaks — and keeps pointing after a live language switch, because the
 * effect re-runs on `locale`.
 *
 * `has(key)` is what makes this safe. Returning `undefined` for a key this app
 * has no translation for leaves the kit's own default in place, so a key added
 * to the kit before it is added to the catalogues degrades to Persian rather
 * than to a humanised key fragment.
 *
 * It renders nothing. It is mounted directly inside `I18nProvider` so that it
 * sees the same context every screen does, and it uses a LAYOUT effect so the
 * kit is wired before the browser paints the first frame rather than one frame
 * after it.
 */
export default function KitStrings() {
  const { t, tList, has, formatDigits, locale } = useTranslation();

  useLayoutEffect(() => {
    setUiStrings({
      string: (key) => (has(key) ? t(key) : undefined),
      list: (key) => (has(key) ? tList(key) : undefined),
      // `formatDigits`, NOT `formatNumber`. What the kit hands over is a
      // transliteration job, not a formatting one: it passes «۱۴۰۴/۰۵/۱۲»,
      // a zero-padded "05" and a bare year through the same call. A number
      // formatter would group the year into "1,404", strip the padding off the
      // hour, and answer empty for the slashed date — which is the string the
      // picker's own parser has to read back.
      number: (value) => formatDigits(value),
    });
    // Nothing unwires it on unmount: this component lives as long as the app
    // does, and clearing the registry on the way out would repaint the kit in
    // Persian during teardown.
  }, [locale, t, tList, has, formatDigits]);

  return null;
}
